<template>
  <div class="table-wrapper">
    <v-skeleton-loader
      class="story-table-skeleton"
      v-if="showLoadingProgress"
      :loading="showLoadingProgress"
      type="table-thead, table-tbody"
    />
    <v-data-table
      v-else-if="
        !showLoadingProgress && sortableNews && sortableNews.length > 0
      "
      :headers="tableHeaders"
      :items="sortableNews"
      class="story-table"
      :class="reorderPermission ? 'table-reorder' : 'table-fixed'"
      disable-pagination
      fixed-header
      disable-sort
      hide-default-footer
      :mobile-breakpoint="0"
    >
      <template
        v-for="(h, i) in tableHeaders"
        v-slot:[`header.${h.value}`]="{}"
      >
        <v-tooltip bottom :key="i">
          <template v-slot:activator="{ on }">
            <span v-on="on">{{ h.text }}</span>
          </template>
          <span>{{ h.fullText }}</span>
        </v-tooltip>
      </template>
      <template v-slot:body="{ items }">
        <draggable
          :list="sortableNews"
          tag="tBody"
          :animation="200"
          :disabled="!isDraggeable"
          ghost-class="draggable-ghost"
          @start="drag = true"
          @end="handleDragEnd"
        >
          <template v-for="item in items">
            <tr :key="item.story_id">
              <td>
                <v-menu open-on-hover top transition="slide-x-transition">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="graymain"
                      v-bind="attrs"
                      v-on="on"
                      icon
                      depressed
                    >
                      <v-icon>more_vert</v-icon>
                    </v-btn>
                  </template>

                  <div class="dropdown">
                    <div class="dropdown-header">
                      Ações disponíveis
                    </div>

                    <div class="dropdown-content">
                      <v-list>
                        <v-list-item
                          v-if="writePermission"
                          ripple
                          @click="createStory(item, sortableNews.indexOf(item))"
                        >
                          <v-icon left>mdi-table-row-plus-before</v-icon>
                          {{ 'Criar retranca antes' }}
                        </v-list-item>

                        <v-list-item
                          v-if="writePermission"
                          ripple
                          @click="
                            createStory(item, sortableNews.indexOf(item) + 1)
                          "
                        >
                          <v-icon left>mdi-table-row-plus-after</v-icon>
                          {{ 'Criar retranca depois' }}
                        </v-list-item>
                      </v-list>
                    </div>

                    <div class="dropdown-footer">
                      <v-list
                        :title="
                          item.is_clickable
                            ? false
                            : 'Essa retranca foi protegida pelo autor e somente pode ser visualizada no iNews com a senha.'
                        "
                      >
                        <v-list-item
                          :disabled="!item.is_clickable"
                          ripple
                          @click="handleRowClick(item)"
                        >
                          <v-icon v-if="item.is_clickable" left
                            >mdi-open-in-new</v-icon
                          >
                          <v-icon v-else left>lock</v-icon>
                          {{ 'Visualizar retranca' }}
                        </v-list-item>
                      </v-list>
                    </div>
                  </div>
                </v-menu>
              </td>
              <td
                :key="index"
                class="text-start"
                v-for="(data, index) in item.data"
              >
                <div>
                  {{ displayedData(data) }}
                </div>
              </td>
            </tr>
          </template>
        </draggable>
      </template>
    </v-data-table>
    <v-snackbar
      v-model="snackbar"
      :multi-line="true"
      :timeout="snackbarTimeout"
      bottom
      left
    >
      {{ this.feedbackMessage }}
      <v-btn color="primary" text @click="hideSnackbar()">Entendi</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import _ from 'lodash';
import { getEnvVar } from '@/utils/getEnvVar';
import { mapState } from 'vuex';
import { storyModes } from '../../utils/mappers';

const TABLE_HEADER_LENGTH_LIMIT = 20;

export default {
  name: 'StoryTable',
  props: {
    folderNews: Array,
    fieldMapping: Array,
    loadingTable: Boolean,
    isFetchingFields: Boolean,
    reorderPermission: Boolean,
    writePermission: Boolean,
    snackbarBox: Boolean,
    feedbackMessage: String,
  },
  components: {
    draggable,
  },
  data() {
    return {
      sortableNews: [],
      drag: false,
      snackbarTimeout: 6000,
      sendReorderedNews: _.debounce(
        function() {
          this.$emit('reorderNews', this.sortableNews);
        }.bind(this),
        parseInt(getEnvVar('VUE_APP_REORDER_DELAY')),
      ),
    };
  },
  beforeDestroy() {
    this.sendReorderedNews.flush();
  },
  watch: {
    loadingTable() {
      this.loadTableData();
    },
    isFetchingFields() {
      this.loadTableData();
    },
  },
  computed: {
    ...mapState({
      currentUser: state => state.user.currentUser,
    }),
    showLoadingProgress() {
      return this.loadingTable || this.isFetchingFields;
    },

    tableHeaders() {
      if (!this.folderNews || this.folderNews.length < 1) {
        return [];
      }
      const headers = [{ text: '', value: 'data-table-expand' }];
      for (const key of this.folderNews[0].data) {
        const text =
          key.label.length > TABLE_HEADER_LENGTH_LIMIT
            ? key.label.substring(0, TABLE_HEADER_LENGTH_LIMIT) + '...'
            : key.label;
        headers.push({
          text: text,
          fullText: key.label,
          class: 'header-text',
          value: key.field,
        });
      }
      return headers;
    },
    snackbar: {
      get() {
        return this.snackbarBox;
      },
      set(value) {
        this.$store.dispatch('news/snackbarTrigger', value);
      },
    },
    isDraggeable() {
      return this.reorderPermission && this.$vuetify.breakpoint.lgAndUp;
    },
  },
  methods: {
    // tableHeight() {
    //   return window.innerHeight - 112;
    // },
    loadTableData() {
      if (!this.loadingTable && !this.isFetchingFields) {
        this.sortableNews = [...this.folderNews];

        // populate booleans whether stories are protected or not
        this.sortableNews.forEach(el => {
          el.is_clickable = this.shouldAllowStoryClick(el);
        });
      }
    },
    createStory(story, index) {
      this.$emit('newStory', story, index);
    },
    displayedData(field) {
      const extraFieldInfo = this.fieldMapping.find(
        item => item.field === field.field,
      );
      if (extraFieldInfo && extraFieldInfo.type === 'checkboxcell') {
        return field.value === 'true' ? '☑' : '☐';
      } else {
        return field.value;
      }
    },

    handleRowClick(story) {
      this.$store.dispatch('news/setStoryMode', storyModes.VIEW);
      this.$router.push({
        path: `/story/${story.slug_id}`,
        query: { content: 'story', slug_id: story.slug_id },
      });
    },
    handleDragEnd() {
      this.drag = false;
      this.sendReorderedNews();
    },
    hideSnackbar() {
      this.$store.dispatch('news/snackbarTrigger', false);
    },
    shouldAllowStoryClick(item) {
      if (!('is_protected' in item)) return true;
      let modified_by = item.data.find(story => story.field === 'modify-by');
      let created_by = item.data.find(story => story.field === 'create-by');

      modified_by = modified_by ? modified_by.value : null;
      created_by = created_by ? created_by.value : null;
      const loggedUserIsAuthor =
        modified_by === this.currentUser.username ||
        created_by === this.currentUser.username;

      return !item.is_protected || (item.is_protected && loggedUserIsAuthor);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/theme-v2.scss';
@import '@/assets/scss/inputs.scss';

.table-wrapper {
  height: 100vh;
  padding: 0 16px;
  background-color: $color-background;
  overflow: hidden;
  font-family: 'globotipo' !important;

  .story-table-skeleton {
    margin: 88px 0 0 0;
  }

  ::v-deep .v-data-table {
    &.story-table {
      margin: 88px 0 0 0;
      min-width: calc(100vw - 440px);
      overflow: auto;

      ::-webkit-scrollbar-track {
        background-color: rgba($color-black, 0.02);
      }

      ::-webkit-scrollbar {
        scroll-margin: 0 16px;
        width: 8px;
        height: 8px;
        background-color: rgba($color-black, 0.02);
      }

      ::-webkit-scrollbar-thumb {
        background-color: rgba($color-black, 0.24);
      }

      .v-data-table__wrapper {
        height: calc(100vh - 104px);

        @include for-size(desktop-down) {
          height: calc(100vh - 248px);
        }

        table {
          // HEAD
          thead {
            tr {
              th {
                font-size: 0.8em;
                color: $color-black-80;
                letter-spacing: 0.08em;
                box-shadow: inset 0 -2px 0 rgb(0 0 0 / 8%);
              }
            }
          }

          // BODY
          tbody {
            tr {
              background-color: $color-white;
              will-change: background-color;
              transition: background-color 0.4s ease-in-out;
              cursor: pointer;

              &:hover {
                background-color: rgba($color-primary, 0.04);
                will-change: background-color;
                transition: background-color 0.4s ease-in-out;

                td {
                  color: $color-primary;
                  will-change: color;
                  transition: color 0.4s ease-in-out;
                }
              }

              td {
                color: $color-black-80;
                font-size: 0.88em;
                border-bottom: solid 1px $color-black-08;
                will-change: color;
                transition: color 0.4s ease-in-out;
              }
            }

            .draggable-ghost {
              opacity: 0.4;
              background-color: rgba($color-primary, 0.5);
            }
          }
        }
      }
    }
  }
}
</style>
